<script lang="ts">
  import Icon from '../icons/Icon.svelte';
  const proofs = [
    { label: 'Generated locally via WebCrypto', meta: 'RFC 6238 · 5/5' },
    { label: 'Vault encrypted at rest', meta: 'AES-GCM-256 · PBKDF2 210K' },
    { label: 'Zero third-party scripts at source', meta: 'grep -r origins · 0' },
    { label: 'localStorage scope', meta: 'vu:theme · only' },
    { label: 'Reproducible build · SRI verified', meta: 'SHA-384 hash pinned' }
  ];
  const lines = [
    { pfx: '$', text: 'vuauth status --network' },
    { pfx: '→', text: 'scanning fetch / WebSocket / RTC peers...' },
    { pfx: '→', text: 'scanning service worker registrations...' },
    { pfx: '→', text: 'scanning third-party origins...' }
  ];
</script>

<section id="level0" class="section scroll-reveal">
  <div class="wrap">
    <div class="grid">
      <div>
        <span class="eyebrow">Level 0</span>
        <h2 class="h-section heading">
          Mathematically incapable, <em>not policy-promised.</em>
        </h2>
        <p class="lede">
          Most "private" apps promise not to look. VuAuth removes the ability to look. The server
          doesn't decrypt your secrets because there is no server. The code isn't shipped through a
          CDN that could swap it because it's verified by Subresource Integrity. The architecture is
          the proof.
        </p>
        <div class="proof-list">
          {#each proofs as p (p.label)}
            <div class="proof-item">
              <span class="proof-icon">
                <Icon name="check" size={14} />
              </span>
              <span class="proof-label">{p.label}</span>
              <span class="proof-meta">{p.meta}</span>
            </div>
          {/each}
        </div>
      </div>

      <div class="terminal" aria-label="Network egress monitor">
        <div class="terminal-bar">
          <span class="dot"></span><span class="dot"></span><span class="dot"></span>
          <span class="title">vuauth · network egress monitor</span>
        </div>
        {#each lines as line, i (i)}
          <span class="line"><span class="pfx">{line.pfx}</span>{line.text}</span>
        {/each}
        <span class="line">
          <span class="pfx">[+]</span>
          <span class="ok"> packets sent today: </span>
          <span class="num">0</span>
        </span>
        <span class="line">
          <span class="pfx">[+]</span>
          <span class="ok"> third-party origins: </span>
          <span class="num">0</span>
        </span>
        <span class="line">
          <span class="pfx">[+]</span>
          <span class="ok"> telemetry events: </span>
          <span class="num">0</span>
        </span>
        <span class="line">
          <span class="pfx">[+]</span>
          <span class="ok"> auto-update channels: </span>
          <span class="key">user-consented</span>
        </span>
        <span class="line">
          <span class="pfx">[+]</span>
          <span class="ok"> RFC 6238 vectors: </span>
          <span class="num">5/5 PASS</span>
        </span>
        <span class="line">
          <span class="pfx">$</span>
          <span class="cursor"></span>
        </span>
      </div>
    </div>
  </div>
</section>

<style>
  .section {
    padding: 96px 0;
    position: relative;
  }
  @media (max-width: 640px) {
    .section {
      padding: 64px 0;
    }
  }
  :global([data-theme='brutalist']) .section {
    border-bottom: 2px solid var(--text);
  }
  .wrap {
    max-width: var(--max-w);
    margin: 0 auto;
    padding: 0 var(--gutter);
  }
  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 64px;
    align-items: center;
  }
  @media (max-width: 960px) {
    .grid {
      grid-template-columns: 1fr;
      gap: 48px;
    }
  }
  .eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--accent);
    padding: 6px 12px;
    border: 1px solid var(--border-2);
    border-radius: 999px;
    background: var(--bg-card);
  }
  :global([data-theme='brutalist']) .eyebrow {
    border-radius: 0;
    border: 2px solid var(--text);
    background: var(--accent-dim);
    color: var(--text);
    font-weight: 800;
  }
  .heading {
    margin: 16px 0 24px;
  }
  .h-section {
    font-size: clamp(32px, 5vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1;
  }
  .h-section em {
    font-family: var(--font-serif);
    font-style: italic;
    font-weight: 400;
    color: var(--accent);
  }
  :global([data-theme='brutalist']) .h-section {
    font-weight: 900;
  }
  :global([data-theme='brutalist']) .h-section em {
    color: var(--text);
  }
  .lede {
    font-size: clamp(16px, 1.7vw, 20px);
    color: var(--text-2);
    max-width: 640px;
    line-height: 1.55;
  }
  .proof-list {
    display: grid;
    gap: 14px;
    margin-top: 32px;
  }
  .proof-item {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 16px;
    align-items: center;
    padding: 18px 22px;
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    font-size: 14px;
  }
  :global([data-theme='brutalist']) .proof-item {
    border: 2px solid var(--text);
    box-shadow: 3px 3px 0 var(--text);
  }
  .proof-icon {
    width: 32px;
    height: 32px;
    display: grid;
    place-items: center;
    background: var(--good);
    color: var(--bg);
    border-radius: 50%;
  }
  :global([data-theme='brutalist']) .proof-icon {
    border-radius: 0;
    border: 2px solid var(--text);
    color: var(--text);
  }
  .proof-label {
    color: var(--text);
    font-weight: 600;
  }
  .proof-meta {
    font-family: var(--font-mono);
    font-size: 12px;
    color: var(--text-3);
  }
  :global([data-theme='brutalist']) .proof-meta {
    color: var(--text);
    font-weight: 600;
  }
  .terminal {
    background: #000;
    color: #00d97c;
    border-radius: var(--radius-lg);
    border: 1px solid var(--border-2);
    padding: 22px 24px;
    font-family: var(--font-mono);
    font-size: 13px;
    line-height: 1.7;
    box-shadow: var(--shadow);
  }
  :global([data-theme='brutalist']) .terminal {
    border: 3px solid var(--text);
    box-shadow: 8px 8px 0 var(--text);
    border-radius: 0;
  }
  .terminal-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
  }
  :global([data-theme='brutalist']) .dot {
    border-radius: 0;
  }
  .terminal-bar .title {
    color: rgba(255, 255, 255, 0.4);
    font-size: 11px;
    margin-left: 12px;
    letter-spacing: 0.04em;
  }
  .line {
    display: block;
  }
  .line .pfx {
    color: rgba(255, 255, 255, 0.4);
    margin-right: 8px;
  }
  .ok {
    color: #00d97c;
  }
  .key {
    color: #00d4ff;
  }
  .num {
    color: #ffb800;
  }
  .cursor {
    display: inline-block;
    width: 8px;
    height: 14px;
    background: #00d97c;
    vertical-align: middle;
    margin-left: 4px;
    animation: blink 1s steps(1) infinite;
  }
  @keyframes blink {
    0%,
    49% {
      opacity: 1;
    }
    50%,
    100% {
      opacity: 0;
    }
  }
</style>
